import React, { useState, useEffect } from 'react';
import { engineerAPI } from '../services/api';

const Engineers = () => {
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    name:'', phone:'', email:'',
    password:'', vehicle_number:'',
    vehicle_type:'',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [resetPass, setResetPass] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadEngineers();
  }, []);

  const loadEngineers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await engineerAPI.getAll(params);
      setEngineers(res.data.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!form.name || !form.phone ||
        !form.password) {
      setError(
        'Name, phone and password required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await engineerAPI.add(form);
      setForm({
        name:'', phone:'', email:'',
        password:'', vehicle_number:'',
        vehicle_type:'',
      });
      setShowAdd(false);
      await loadEngineers();
    } catch (err) {
      setError(
        err.response?.data?.message
        || 'Failed to add engineer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm(
      'Deactivate this engineer?')) return;
    try {
      await engineerAPI.deactivate(id);
      await loadEngineers();
      if (selected?.id === id)
        setSelected(null);
    } catch (err) {
      alert('Failed to deactivate');
    }
  };

  const handleResetPassword = async (id) => {
    if (!resetPass || resetPass.length < 6) {
      alert('Password must be 6+ characters');
      return;
    }
    setResetting(true);
    try {
      await engineerAPI.resetPassword(id,
        { new_password: resetPass });
      setResetPass('');
      alert('Password reset successfully!');
    } catch (err) {
      alert('Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const statusColor = (status) => {
    if (status === 'active')
      return 'var(--green)';
    if (status === 'idle')
      return 'var(--yellow)';
    return 'var(--text3)';
  };

  const inputStyle = {
    width:'100%',
    background:'var(--surface2)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'10px 14px',
    borderRadius:'8px',
    fontSize:'13.5px',
    outline:'none',
  };

  const labelStyle = {
    display:'block',
    fontSize:'11px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'1px',
    textTransform:'uppercase',
    marginBottom:'6px',
  };

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Field Engineers
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            Manage your field engineers
          </p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          style={{
            background:'var(--accent)',
            color:'var(--bg)',
            border:'none',
            padding:'10px 20px',
            borderRadius:'8px',
            fontSize:'13px',
            fontWeight:'600',
            cursor:'pointer',
          }}
        >
          + Add Engineer
        </button>
      </div>

      {/* Add Engineer Form */}
      {showAdd && (
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          padding:'24px',
          marginBottom:'20px',
        }}>
          <div style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'15px', fontWeight:'700',
            marginBottom:'20px',
          }}>
            Add New Engineer
          </div>

          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'16px',
          }}>
            <div>
              <label style={labelStyle}>
                Full Name *
              </label>
              <input
                style={inputStyle}
                placeholder="Engineer name"
                value={form.name}
                onChange={e => setForm({
                  ...form, name:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Phone Number *
              </label>
              <input
                style={inputStyle}
                placeholder="10 digit phone"
                value={form.phone}
                onChange={e => setForm({
                  ...form, phone:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Email
              </label>
              <input
                style={inputStyle}
                placeholder="Email (optional)"
                value={form.email}
                onChange={e => setForm({
                  ...form, email:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Password *
              </label>
              <input
                type="password"
                style={inputStyle}
                placeholder="Min 6 characters"
                value={form.password}
                onChange={e => setForm({
                  ...form,
                  password:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Vehicle Number
              </label>
              <input
                style={inputStyle}
                placeholder="e.g. MH02AB1234"
                value={form.vehicle_number}
                onChange={e => setForm({
                  ...form,
                  vehicle_number:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Vehicle Type
              </label>
              <select
                style={inputStyle}
                value={form.vehicle_type}
                onChange={e => setForm({
                  ...form,
                  vehicle_type:e.target.value
                })}
              >
                <option value="">
                  Select type
                </option>
                <option value="Bike">Bike</option>
                <option value="Scooter">
                  Scooter
                </option>
                <option value="Car">Car</option>
                <option value="Van">Van</option>
                <option value="Auto">Auto</option>
              </select>
            </div>
          </div>

          {error && (
            <div style={{
              background:
                'rgba(255,77,109,0.1)',
              border:
                '1px solid rgba(255,77,109,0.3)',
              color:'var(--red)',
              padding:'10px 14px',
              borderRadius:'8px',
              fontSize:'13px',
              marginTop:'16px',
            }}>
              {error}
            </div>
          )}

          <div style={{
            display:'flex',
            gap:'10px',
            marginTop:'20px',
          }}>
            <button
              onClick={handleAdd}
              disabled={saving}
              style={{
                background:'var(--accent)',
                color:'var(--bg)',
                border:'none',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                fontWeight:'600',
                cursor: saving
                  ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...'
                : 'Save Engineer'}
            </button>
            <button
              onClick={() => {
                setShowAdd(false);
                setError('');
              }}
              style={{
                background:'transparent',
                color:'var(--text2)',
                border:
                  '1px solid var(--border2)',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                cursor:'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{
        display:'flex', gap:'10px',
        marginBottom:'16px',
      }}>
        <div style={{
          display:'flex',
          alignItems:'center',
          background:'var(--surface2)',
          border:'1px solid var(--border2)',
          borderRadius:'8px',
          padding:'0 12px',
          gap:'8px', flex:1,
          maxWidth:'300px',
        }}>
          <span style={{
            color:'var(--text3)',
          }}>⊞</span>
          <input
            placeholder="Search engineers..."
            value={search}
            onChange={e =>
              setSearch(e.target.value)}
            onKeyPress={e =>
              e.key === 'Enter' &&
              loadEngineers()}
            style={{
              background:'none',
              border:'none', outline:'none',
              color:'var(--text)',
              fontSize:'13px',
              padding:'9px 0', flex:1,
            }}
          />
        </div>
        <button onClick={loadEngineers}
          style={{
            background:'var(--surface2)',
            border:'1px solid var(--border2)',
            color:'var(--text2)',
            padding:'8px 16px',
            borderRadius:'8px',
            fontSize:'13px',
            cursor:'pointer',
          }}
        >
          Search
        </button>
      </div>

      {/* Main Layout */}
      <div style={{
        display:'grid',
        gridTemplateColumns:
          selected ? '1fr 340px' : '1fr',
        gap:'16px',
      }}>
        {/* Engineers Grid */}
        <div>
          {loading ? (
            <div style={{
              padding:'40px',
              textAlign:'center',
              color:'var(--text3)',
              fontFamily:'DM Mono,monospace',
            }}>
              Loading engineers...
            </div>
          ) : engineers.length === 0 ? (
            <div style={{
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'12px',
              padding:'60px',
              textAlign:'center',
              color:'var(--text3)',
            }}>
              <div style={{
                fontSize:'40px',
                marginBottom:'12px',
              }}>
                ◉
              </div>
              <div style={{
                fontSize:'14px',
                color:'var(--text2)',
                marginBottom:'6px',
              }}>
                No engineers yet
              </div>
              <div style={{
                fontSize:'12px',
              }}>
                Click "+ Add Engineer"
                to add your first engineer
              </div>
            </div>
          ) : (
            <div style={{
              display:'grid',
              gridTemplateColumns:
                'repeat(auto-fill, minmax(280px, 1fr))',
              gap:'14px',
            }}>
              {engineers.map(eng => (
                <div key={eng.id}
                  onClick={() =>
                    setSelected(
                      selected?.id === eng.id
                        ? null : eng
                    )}
                  style={{
                    background:'var(--surface)',
                    border: selected?.id === eng.id
                      ? '1px solid var(--accent)'
                      : '1px solid var(--border)',
                    borderRadius:'12px',
                    padding:'18px',
                    cursor:'pointer',
                    transition:'all 0.15s',
                  }}
                >
                  {/* Card Header */}
                  <div style={{
                    display:'flex',
                    alignItems:'center',
                    gap:'12px',
                    marginBottom:'14px',
                  }}>
                    <div style={{
                      width:'44px',
                      height:'44px',
                      borderRadius:'10px',
                      background:
                        'linear-gradient(135deg, var(--accent), var(--purple))',
                      display:'flex',
                      alignItems:'center',
                      justifyContent:'center',
                      fontFamily:
                        'Syne,sans-serif',
                      fontSize:'16px',
                      fontWeight:'800',
                      color:'var(--bg)',
                    }}>
                      {eng.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()}
                    </div>
                    <div>
                      <div style={{
                        fontFamily:
                          'Syne,sans-serif',
                        fontSize:'15px',
                        fontWeight:'700',
                      }}>
                        {eng.name}
                      </div>
                      <div style={{
                        fontSize:'11px',
                        fontFamily:
                          'DM Mono,monospace',
                        color:'var(--text3)',
                        marginTop:'2px',
                      }}>
                        {eng.employee_id}
                      </div>
                    </div>
                    <div style={{
                      marginLeft:'auto',
                      display:'flex',
                      alignItems:'center',
                      gap:'5px',
                      fontSize:'11px',
                      fontFamily:
                        'DM Mono,monospace',
                      color: statusColor(
                        eng.current_status),
                    }}>
                      <div style={{
                        width:'6px',
                        height:'6px',
                        borderRadius:'50%',
                        background: statusColor(
                          eng.current_status),
                      }} />
                      {eng.current_status}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{
                    display:'grid',
                    gridTemplateColumns:
                      '1fr 1fr',
                    gap:'8px',
                    marginBottom:'12px',
                  }}>
                    <div style={{
                      background:
                        'var(--surface2)',
                      borderRadius:'8px',
                      padding:'8px',
                      textAlign:'center',
                    }}>
                      <div style={{
                        fontFamily:
                          'DM Mono,monospace',
                        fontSize:'18px',
                        color:'var(--accent)',
                      }}>
                        {eng.completed_tickets
                          || 0}
                      </div>
                      <div style={{
                        fontSize:'10px',
                        color:'var(--text3)',
                        marginTop:'2px',
                      }}>
                        Completed
                      </div>
                    </div>
                    <div style={{
                      background:
                        'var(--surface2)',
                      borderRadius:'8px',
                      padding:'8px',
                      textAlign:'center',
                    }}>
                      <div style={{
                        fontFamily:
                          'DM Mono,monospace',
                        fontSize:'18px',
                        color:'var(--yellow)',
                      }}>
                        {eng.active_tickets || 0}
                      </div>
                      <div style={{
                        fontSize:'10px',
                        color:'var(--text3)',
                        marginTop:'2px',
                      }}>
                        Active
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div style={{
                    display:'flex',
                    justifyContent:
                      'space-between',
                    alignItems:'center',
                    paddingTop:'12px',
                    borderTop:
                      '1px solid var(--border)',
                    fontSize:'12px',
                  }}>
                    <span style={{
                      color:'var(--text3)',
                      fontFamily:
                        'DM Mono,monospace',
                    }}>
                      {eng.phone}
                    </span>
                    {eng.vehicle_type && (
                      <span style={{
                        background:
                          'var(--surface3)',
                        color:'var(--text2)',
                        padding:'2px 8px',
                        borderRadius:'4px',
                        fontSize:'11px',
                      }}>
                        {eng.vehicle_type}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selected && (
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            overflow:'hidden',
            height:'fit-content',
          }}>
            <div style={{
              padding:'16px 20px',
              borderBottom:
                '1px solid var(--border)',
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
            }}>
              <div style={{
                fontFamily:'Syne,sans-serif',
                fontSize:'14px',
                fontWeight:'700',
              }}>
                Engineer Details
              </div>
              <button
                onClick={() =>
                  setSelected(null)}
                style={{
                  background:'none',
                  border:'none',
                  color:'var(--text3)',
                  cursor:'pointer',
                  fontSize:'18px',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding:'20px' }}>
              {/* Info */}
              {[
                ['Name', selected.name],
                ['Employee ID',
                  selected.employee_id],
                ['Phone', selected.phone],
                ['Email',
                  selected.email || '—'],
                ['Vehicle',
                  selected.vehicle_number
                  || '—'],
                ['Type',
                  selected.vehicle_type
                  || '—'],
                ['Status',
                  selected.current_status],
                ['Last Ping',
                  selected.last_ping_at
                    ? new Date(
                        selected.last_ping_at
                      ).toLocaleString('en-IN')
                    : 'Never'],
              ].map(([label, value]) => (
                <div key={label} style={{
                  display:'flex',
                  justifyContent:
                    'space-between',
                  padding:'8px 0',
                  borderBottom:
                    '1px solid var(--border)',
                  fontSize:'12.5px',
                }}>
                  <span style={{
                    color:'var(--text3)',
                    fontFamily:
                      'DM Mono,monospace',
                    fontSize:'11px',
                  }}>
                    {label}
                  </span>
                  <span style={{
                    color:'var(--text)',
                    fontWeight:'500',
                  }}>
                    {value}
                  </span>
                </div>
              ))}

              {/* Reset Password */}
              <div style={{
                marginTop:'20px',
                padding:'16px',
                background:'var(--surface2)',
                borderRadius:'8px',
              }}>
                <div style={{
                  fontSize:'11px',
                  fontFamily:
                    'DM Mono,monospace',
                  color:'var(--text3)',
                  letterSpacing:'1px',
                  marginBottom:'10px',
                }}>
                  RESET PASSWORD
                </div>
                <input
                  type="password"
                  placeholder="New password"
                  value={resetPass}
                  onChange={e =>
                    setResetPass(e.target.value)}
                  style={{
                    width:'100%',
                    background:'var(--surface)',
                    border:
                      '1px solid var(--border2)',
                    color:'var(--text)',
                    padding:'9px 12px',
                    borderRadius:'6px',
                    fontSize:'13px',
                    outline:'none',
                    marginBottom:'8px',
                  }}
                />
                <button
                  onClick={() =>
                    handleResetPassword(
                      selected.id)}
                  disabled={resetting}
                  style={{
                    width:'100%',
                    background:
                      'rgba(0,212,255,0.1)',
                    color:'var(--accent)',
                    border:
                      '1px solid rgba(0,212,255,0.3)',
                    padding:'8px',
                    borderRadius:'6px',
                    fontSize:'12px',
                    fontWeight:'600',
                    cursor:'pointer',
                  }}
                >
                  {resetting
                    ? 'Resetting...'
                    : 'Reset Password'}
                </button>
              </div>

              {/* Deactivate */}
              {selected.is_active && (
                <button
                  onClick={() =>
                    handleDeactivate(
                      selected.id)}
                  style={{
                    width:'100%',
                    background:
                      'rgba(255,77,109,0.1)',
                    color:'var(--red)',
                    border:
                      '1px solid rgba(255,77,109,0.3)',
                    padding:'10px',
                    borderRadius:'8px',
                    fontSize:'13px',
                    fontWeight:'600',
                    cursor:'pointer',
                    marginTop:'10px',
                  }}
                >
                  Deactivate Engineer
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Engineers;